import React, { useState, useRef } from "react";
import axios from "axios";
import { Upload } from "lucide-react";

const API_BASE_URL = process.env.REACT_APP_FASTAPI_BASE_URL || process.env.REACT_APP_API_BASE_URL;

const postProcessPdf = async (formData) => {
  try {
    return await axios.post(`${API_BASE_URL}/process_pdf/`, formData, {
      headers: { "Content-Type": "multipart/form-data" },
    });
  } catch (primaryError) {
    return axios.post(`${API_BASE_URL}/api/process_pdf/`, formData, {
      headers: { "Content-Type": "multipart/form-data" },
    });
  }
};

const Translate = () => {
  const [file, setFile] = useState(null);
  const [pdfUrl, setPdfUrl] = useState(null);
  const [language, setLanguage] = useState("");
  const [question, setQuestion] = useState("");
  const [result, setResult] = useState("");
  const [error, setError] = useState("");
  const fileInputRef = useRef(null);

  const handleFileChange = (e) => {
    const selectedFile = e.target.files[0];
    if (!selectedFile) return;
    setFile(selectedFile);
    setPdfUrl(URL.createObjectURL(selectedFile));
    setError("");
  };

  const handleDragOver = (event) => {
    event.preventDefault();
  };

  const handleDrop = (event) => {
    event.preventDefault();
    if (event.dataTransfer.files && event.dataTransfer.files[0]) {
      const droppedFile = event.dataTransfer.files[0];
      setFile(droppedFile);
      setPdfUrl(URL.createObjectURL(droppedFile));
      setError("");
    }
  };

  const clearSelectedFile = () => {
    setFile(null);
    setPdfUrl(null);
  };

  const handleBrowseClick = () => {
    fileInputRef.current?.click();
  };

  const handleTranslate = async () => {
    if (!file || !language) {
      alert("Please select a file and a language.");
      return;
    }
    const formData = new FormData();
    formData.append("file", file);
    formData.append("query", question || "Summarise the document");
    formData.append("translation_language", language);

    try {
      const response = await postProcessPdf(formData);
      setResult(response.data.translated_answer || "No translation available.");
      setError("");
    } catch (error) {
      console.error("Error translating document:", error);
      setError("Error translating document. Please try again.");
    }
  };

  const handleSummarize = async () => {
    if (!file) {
      alert("Please select a file.");
      return;
    }
    const formData = new FormData();
    formData.append("file", file);
    formData.append("query", question || "Summarize the document");

    try {
      const response = await postProcessPdf(formData);
      setResult(response.data.answer || "No summary available.");
      setError("");
    } catch (error) {
      console.error("Error summarizing document:", error);
      setError("Error summarizing document. Please try again.");
    }
  };

  const handleSimplify = async () => {
    if (!file) {
      alert("Please select a file.");
      return;
    }
    const formData = new FormData();
    formData.append("file", file);
    formData.append("query", "Simplify the language of the document");

    try {
      const response = await postProcessPdf(formData);
      setResult(response.data.answer || "No simplified version available.");
      setError("");
    } catch (error) {
      console.error("Error simplifying document:", error);
      setError("Error simplifying document. Please try again.");
    }
  };

  const handleGetAnswer = async () => {
    if (!file || !question) {
      alert("Please select a file and enter a question.");
      return;
    }
    const formData = new FormData();
    formData.append("file", file);
    formData.append("query", question);

    try {
      const response = await postProcessPdf(formData);
      setResult(response.data.answer || "No answer available.");
      setError("");
    } catch (error) {
      console.error("Error getting answer:", error);
      setError("Error getting answer. Please try again.");
    }
  };

  return (
    <div className="h-full p-6 flex flex-col gap-4 overflow-hidden">
      <div className="bg-gradient-to-b from-white/70 via-white/40 to-white/30 backdrop-blur-xl border border-white/40 shadow-[0_10px_25px_rgba(0,0,0,0.08)] rounded-3xl px-6 py-4">
        <h1 className="text-2xl md:text-3xl font-semibold text-gray-800">Document Translation</h1>
        <p className="text-gray-600 mt-1">Translate, summarize, simplify, or ask questions on uploaded legal documents.</p>
      </div>
      <div className="w-full flex flex-col xl:flex-row gap-4 flex-1 min-h-0">
        {/* Document Upload and Preview */}
        <div className="xl:w-1/2 bg-gradient-to-b from-white/70 via-white/40 to-white/30 backdrop-blur-xl border border-white/40 shadow-[0_10px_25px_rgba(0,0,0,0.08)] rounded-3xl p-6 min-h-0 flex flex-col">
          {file ? (
            <div className="space-y-4 flex-1 min-h-0 flex flex-col">
              <div className="flex items-center justify-between">
                <p className="text-gray-600 text-sm truncate">Selected: {file.name}</p>
                <button
                  onClick={clearSelectedFile}
                  className="text-sm text-orange-600 hover:text-orange-700"
                >
                  Remove
                </button>
              </div>
              <div className="border border-gray-200 rounded-2xl overflow-hidden bg-white/70 flex-1 min-h-[260px]">
                {pdfUrl ? (
                  <iframe
                    src={pdfUrl}
                    className="w-full h-full border-none"
                    title="PDF Viewer"
                  />
                ) : (
                  <p className="text-gray-500 text-center mt-8">Preview unavailable.</p>
                )}
              </div>
            </div>
          ) : (
            <div
              className="border-2 border-dashed border-gray-300 rounded-3xl p-12 text-center cursor-pointer hover:border-[#f97316] transition-colors"
              onDragOver={handleDragOver}
              onDrop={handleDrop}
              onClick={handleBrowseClick}
            >
              <Upload className="mx-auto text-gray-400 mb-6" size={64} />
              <p className="text-gray-600 text-xl mb-2">
                Drag and Drop files to upload
              </p>
              <p className="text-gray-600 text-xl mb-6">or</p>
              <button className="bg-gradient-to-r from-orange-400 to-orange-600 text-white px-8 py-3 rounded-xl text-base hover:from-orange-500 hover:to-orange-700 transition duration-300 shadow-sm">
                Browse
              </button>
              <input
                type="file"
                ref={fileInputRef}
                onChange={handleFileChange}
                className="hidden"
                accept=".pdf,.doc,.docx"
              />
            </div>
          )}
        </div>

        {/* Actions and Output */}
        <div className="xl:w-1/2 space-y-4 bg-gradient-to-b from-white/70 via-white/40 to-white/30 backdrop-blur-xl border border-white/40 shadow-[0_10px_25px_rgba(0,0,0,0.08)] rounded-3xl p-6 min-h-0 flex flex-col">
          {/* Action Buttons */}
          <div className="flex flex-wrap gap-2">
            {["Translate", "Summarize", "Simplify Language", "Get Answer"].map(
              (action) => (
                <button
                  key={action}
                  onClick={() => {
                    switch (action) {
                      case "Translate":
                        handleTranslate();
                        break;
                      case "Summarize":
                        handleSummarize();
                        break;
                      case "Simplify Language":
                        handleSimplify();
                        break;
                      case "Get Answer":
                        handleGetAnswer();
                        break;
                      default:
                        break;
                    }
                  }}
                  className="px-4 py-2 rounded-xl transition-colors duration-200 bg-white/70 border border-gray-200 text-gray-700 hover:bg-gradient-to-r hover:from-orange-400 hover:to-orange-600 hover:text-white"
                >
                  {action}
                </button>
              )
            )}
          </div>

          {/* Language Selector */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
            <select
              value={language}
              onChange={(e) => setLanguage(e.target.value)}
              className="md:col-span-1 border border-gray-300 rounded-xl px-3 py-2 bg-white/80 focus:outline-none focus:ring-2 focus:ring-[#FF7F50]"
            >
              <option value="">Select Language</option>
              <option value="as">Assamese</option>
              <option value="bn">Bengali</option>
              <option value="gu">Gujarati</option>
              <option value="hi">Hindi</option>
              <option value="kn">Kannada</option>
              <option value="ml">Malayalam</option>
              <option value="mr">Marathi</option>
              <option value="ne">Nepali</option>
              <option value="or">Odia</option>
              <option value="pa">Punjabi</option>
              <option value="ta">Tamil</option>
              <option value="te">Telugu</option>
              <option value="ur">Urdu</option>
            </select>
          </div>

          {/* Question Input */}
          <div className="flex items-center">
            <input
              type="text"
              placeholder="Enter your question here"
              value={question}
              onChange={(e) => setQuestion(e.target.value)}
              className="md:col-span-2 border border-gray-300 rounded-xl px-3 py-2 w-full bg-white/80 focus:outline-none focus:ring-2 focus:ring-[#FF7F50]"
            />
          </div>

          {/* Output Area */}
          <div className="border border-gray-200 bg-white/80 rounded-2xl p-4 flex-1 min-h-0 flex flex-col">
            <h3 className="text-lg font-semibold mb-2">Output</h3>
            <div className="flex-1 min-h-0 overflow-y-auto pr-1">
              <p>{result}</p>
            </div>
            {error && <p className="text-red-500 mt-2">{error}</p>}
          </div>
        </div>
      </div>
    </div>
  );
};

export default Translate;
